import { h } from 'preact';
import styles from './Banner.module.scss';
import { isOnSiteUrl } from '../../utils/isOnSiteUrl';
// Maps 'top-left' → 'topLeft' etc. for CSS module lookup
const positionClassMap = {
    'top-left': 'topLeft',
    'top-center': 'topCenter',
    'top-right': 'topRight',
    'center-left': 'centerLeft',
    'center': 'center',
    'center-right': 'centerRight',
    'bottom-left': 'bottomLeft',
    'bottom-center': 'bottomCenter',
    'bottom-right': 'bottomRight',
};
export function Banner({ banner }) {
    const { imgSrc, link, name, openInNewTab, contentPosition, overlayText, overlaySubText, overlayButtonText, overlayButtonLink } = banner;
    // Parse column and row span from name
    // Supports formats: 
    // - "Banner" (1col, 1row desktop and mobile)
    // - "Banner-2col" (2 cols desktop, 1 mobile)
    // - "Banner-2col-2row" (2 cols, 2 rows desktop, 1 col 1 row mobile)
    // - "Banner-2dcol-2drow-2mcol-2mrow" (2 cols 2 rows desktop, 2 cols 2 rows mobile)
    const getSpans = () => {
        // Check for desktop/mobile specific format first
        const dcolMatch = name.match(/(\d+)dcol/);
        const drowMatch = name.match(/(\d+)drow/);
        const mcolMatch = name.match(/(\d+)mcol/);
        const mrowMatch = name.match(/(\d+)mrow/);
        // If we have desktop/mobile specific values
        if (dcolMatch || drowMatch || mcolMatch || mrowMatch) {
            return {
                desktopColumns: dcolMatch ? parseInt(dcolMatch[1]) : 1,
                desktopRows: drowMatch ? parseInt(drowMatch[1]) : 1,
                mobileColumns: mcolMatch ? parseInt(mcolMatch[1]) : 1,
                mobileRows: mrowMatch ? parseInt(mrowMatch[1]) : 1,
            };
        }
        // Otherwise use legacy format (col/row without d/m prefix)
        const colMatch = name.match(/(\d+)col/);
        const rowMatch = name.match(/(\d+)row/);
        const columns = colMatch ? parseInt(colMatch[1]) : 1;
        const rows = rowMatch ? parseInt(rowMatch[1]) : 1;
        return {
            desktopColumns: columns,
            desktopRows: rows,
            mobileColumns: 1, // Default to 1 column on mobile for legacy format
            mobileRows: 1, // Default to 1 row on mobile for legacy format
        };
    };
    const { desktopColumns, desktopRows, mobileColumns, mobileRows } = getSpans();
    // Build class names for desktop and mobile spanning
    const desktopColumnClass = styles[`banner${desktopColumns}dcol`] || '';
    const desktopRowClass = desktopRows > 1 ? styles[`banner${desktopRows}drow`] : '';
    const mobileColumnClass = styles[`banner${mobileColumns}mcol`] || '';
    const mobileRowClass = mobileRows > 1 ? styles[`banner${mobileRows}mrow`] : '';
    // AQ-1063: Boost auto-generates a redirect URL for every banner, but tiles
    // used for informational content only (e.g. Afterpay messaging) should not
    // navigate anywhere. Off-site links render non-clickable.
    const isClickable = isOnSiteUrl(link);
    const overlayLink = overlayButtonLink || link;
    const isOverlayLinkClickable = isOnSiteUrl(overlayLink);
    const handleClick = () => {
        if (!isClickable)
            return;
        if (openInNewTab) {
            window.open(link, '_blank', 'noopener,noreferrer');
        }
        else {
            window.location.href = link;
        }
    };
    const overlayPositionClass = contentPosition ? styles[positionClassMap[contentPosition]] || '' : '';
    return (h("div", { class: `${styles.banner} ${isClickable ? '' : styles.nonClickable} ${desktopColumnClass} ${desktopRowClass} ${mobileColumnClass} ${mobileRowClass}`.trim(), onClick: isClickable ? handleClick : undefined },
        h("img", { src: imgSrc, alt: name, class: styles.bannerImage }),
        (overlayText || overlaySubText || overlayButtonText) && (h("div", { class: `${styles.overlay} ${overlayPositionClass}`.trim() },
            overlayText && h("p", { class: styles.overlayTitle }, overlayText),
            overlaySubText && h("p", { class: styles.overlaySubText }, overlaySubText),
            overlayButtonText && (h("a", { href: isOverlayLinkClickable ? overlayLink : undefined, class: `${styles.overlayButton} ${isOverlayLinkClickable ? '' : styles.nonClickable}`.trim(), target: openInNewTab ? '_blank' : '_self', rel: "noopener noreferrer", onClick: (e) => e.stopPropagation() },
                h("span", null, overlayButtonText),
                h("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                    h("mask", { id: "mask0_2033_4639", style: "mask-type:alpha", maskUnits: "userSpaceOnUse", x: "0", y: "0", width: "24", height: "24" },
                        h("rect", { width: "24", height: "24", fill: "#D9D9D9" })),
                    h("g", { mask: "url(#mask0_2033_4639)" },
                        h("path", { d: "M13.8462 17.6537L12.7923 16.5692L16.6115 12.7499H4.5V11.2499H16.6115L12.7923 7.43069L13.8462 6.34619L19.5 11.9999L13.8462 17.6537Z", fill: "#F8F8F8" })))))))));
}
